import { formatDateTime, formatCurrency } from "../../helper/formatCurrency";

export const eventDetailsDownloadCSV = ({
  event,
  acceptedGuests,
  declinedGuests,
  pendingGuests,
  refunds,
  getEventStatus,
  formatDate,
  formatDateWithOrdinal,
  usherStats,
}) => {
  const csvSections = [];
  const currentDate = new Date().toLocaleDateString();

  const totalCashGifts = acceptedGuests.reduce(
    (sum, guest) => sum + (guest.cashGiftAmount || 0),
    0
  );

  const totalEventAttire = acceptedGuests.reduce((sum, guest) => {
    if (guest.eventAttire === 0) return sum;
    return sum + (guest.eventAttire?.totalAmount || 0);
  }, 0);

  const totalTickets = acceptedGuests.reduce((sum, guest) => {
    if (guest.ticketFeePaid?.length > 0) {
      return sum + (guest.ticketFeePaid[0]?.totalAmount || 0);
    }
    return sum;
  }, 0);

  const totalRevenue = totalCashGifts + totalEventAttire + totalTickets;

  // ===============================
  // HEADER
  // ===============================
  csvSections.push(",EVENT MANAGEMENT REPORT");
  csvSections.push(`,Generated on, ${currentDate}`);
  csvSections.push(`,Event, ${event?.eventTitle || "Untitled Event"}`);
  csvSections.push("");

  // ===============================
  // SUMMARY STATISTICS
  // ===============================
  csvSections.push(",=== SUMMARY STATISTICS ===");
  csvSections.push(",Metric,Value");
  csvSections.push(
    `,Total Invitations Sent,${
      acceptedGuests.length + declinedGuests.length + pendingGuests.length
    }`
  );
  csvSections.push(`,Accepted Invitations,${acceptedGuests.length}`);
  csvSections.push(`,Declined Invitations,${declinedGuests.length}`);
  csvSections.push(`,Pending Invitations,${pendingGuests.length}`);
  csvSections.push(
    `,Acceptance Rate,${(
      (acceptedGuests.length /
        (acceptedGuests.length +
          declinedGuests.length +
          pendingGuests.length)) *
      100
    ).toFixed(2)}%`
  );
  csvSections.push(`,Total Cash Gifts,${totalCashGifts}`);
  csvSections.push(`,Total Event Attire Sales,${totalEventAttire}`);
  csvSections.push(`,Total Ticket Sales,${totalTickets}`);
  csvSections.push(`,Total Revenue,${totalRevenue}`);
  csvSections.push(`,Refund Requests,${refunds.length}`);
  csvSections.push(
    `,Pending Refunds,${refunds.filter((r) => r.status === "Pending").length}`
  );
  csvSections.push(
    `,Approved Refunds,${
      refunds.filter((r) => r.status === "Approved" || r.status === "Paid")
        .length
    }`
  );
  csvSections.push("");

  // ===============================
  // EVENT DETAILS
  // ===============================
  csvSections.push(",=== EVENT DETAILS ===");
  csvSections.push(",Field,Value");
  csvSections.push(`,Event Title,"${event?.eventTitle || "Untitled Event"}"`);
  csvSections.push(`,Event Type,"${event?.eventType || "Others"}"`);

  csvSections.push(
    `,Event Date,"${formatDateWithOrdinal(
      event?.multipleDates?.[0]?.date || event?.eventDate || event?.startDate
    )}"`
  );
  csvSections.push(
    `,Event Time,"${
      event?.eventTime ||
      event?.startTime ||
      event?.multipleDates?.[0]?.time ||
      "Not specified"
    }"`
  );

  const rawLocation =
    event?.eventLandmark ||
    event?.customAddress ||
    event?.eventLocation ||
    event?.multipleDates?.[0]?.landmark ||
    event?.multipleDates?.[0]?.location ||
    "Not specified";

  // Escape double quotes for CSV
  const cleanLocation = rawLocation.replace(/"/g, '""');

  // Limit to 30 characters
  const shortLocation =
    cleanLocation.length > 30
      ? cleanLocation.slice(0, 30) + "…"
      : cleanLocation;

  csvSections.push(`,Location,"${shortLocation}"`);

  csvSections.push(
    `,Status,"${getEventStatus(
      event?.multipleDates?.[0]?.date || event?.eventDate || event?.startDate
    )}"`
  );
  csvSections.push(
    `,Cash Gift Accepted,"${event?.isCashGiftAccepted ? "Yes" : "No"}"`
  );
  if (event?.minCashGift) {
    csvSections.push(`,Minimum Cash Gift,"${event.minCashGift}"`);
  }
  const rawDescription = event?.eventDescription || "No description available.";

  const cleanDescription = rawDescription.replace(/"/g, '""');

  // Limit to max 20 characters
  const shortDescription =
    cleanDescription.length > 30
      ? cleanDescription.slice(0, 30) + "…"
      : cleanDescription;

  csvSections.push(`,Description,"${shortDescription}"`);

  csvSections.push("");

  // ===============================
  // ACCEPTED GUESTS
  // ===============================
  csvSections.push(",=== ACCEPTED GUESTS ===");
  csvSections.push(
    "S/No,Guest Name,Email,Phone Number,Cash Gift Amount,Event Attire Amount,Ticket Amount,Delivery Address,Response Status,Response Date, Check In Status, Check In Date & Time"
  );

  acceptedGuests.forEach((guest, index) => {
    const ticketAmount =
      guest.ticketFeePaid?.length > 0
        ? guest.ticketFeePaid[0]?.totalAmount || 0
        : 0;

    const eventAttireAmount =
      guest.eventAttire === 0 ? 0 : guest.eventAttire?.totalAmount || 0;

    const responseStatus = guest.rsvpStatus
      ? guest?.rsvpStatus?.charAt(0).toUpperCase() + guest.rsvpStatus.slice(1)
      : "Accepted";

    const checkInStatus =
    guest.isCheckedIn && guest.checkInTime ? "Checked-In" : "Not Checked-In";

    csvSections.push(
      [
        `"${index + 1}"`,
        `"${guest.guestName || guest.name || ""}"`,
        `"${guest.email || ""}"`,
        `"${guest.phone || ""}"`,
        `"${guest.cashGiftAmount || 0}"`,
        `"${eventAttireAmount}"`,
        `"${ticketAmount}"`,
        `"${guest.deliveryAddress || ""}"`,
        `"${responseStatus}"`,
        `"${formatDate(guest.responseDate) || ""}"`,
        `"${checkInStatus}"`,
        `"${formatDateTime(guest.checkInTime) || ""}"`,
      ].join(",")
    );
  });

  csvSections.push("");

  // ===============================
  // USHERS
  // ===============================

  const hasValidUsher =
    Array.isArray(usherStats) &&
    usherStats.some(
      (u) =>
        u &&
        (u.firstName ||
          u.lastName ||
          u.email ||
          u.phoneNumber)
    );

  if (hasValidUsher) {
    csvSections.push(",=== USHERS ===");
    csvSections.push(
      "S/No,Usher Name,Email,Phone Number,Approval Date,Approval Status,Guests Checked In"
    );

    usherStats.forEach((usher, index) => {

      const fullName = [usher.firstName, usher.lastName]
        .filter(Boolean)
        .join(" ");

      // Skip empty placeholder rows
      if (!fullName && !usher.email && !usher.phoneNumber) return;

      const rawApprovalStatus =
        typeof usher?.approvalStatus === "string" ? usher.approvalStatus : "";

      const approvalStatus = rawApprovalStatus
        ? rawApprovalStatus.charAt(0).toUpperCase() +
          rawApprovalStatus.slice(1).toLowerCase()
        : "Pending";

      csvSections.push(
        [
          `"${index + 1}"`,
          `"${fullName}"`,
          `"${usher.email || "—"}"`,
          `"${usher.phoneNumber || "—"}"`,
          `"${formatDate(usher.assignedDate) || "-"}"`,
          `"${approvalStatus}"`,
          `"${usher?.checkedInGuests?.length || 0}"`,
        ].join(",")
      );
    });

    csvSections.push("");
  }

  // ===============================
  // DECLINED GUESTS
  // ===============================
  if (declinedGuests.length > 0) {
    csvSections.push(",=== DECLINED INVITATIONS ===");
    csvSections.push("S/No.,Guest Name,Email,Phone Number,Response Date");

    declinedGuests.forEach((guest, index) => {
      csvSections.push(
        [
          `"${index + 1}"`,
          `"${guest.guestName || guest.name || ""}"`,
          `"${guest.email || ""}"`,
          `"${guest.phone || ""}"`,
          `"${formatDate(guest.responseDate) || ""}"`,
        ].join(",")
      );
    });

    csvSections.push("");
  }

  // ===============================
  // PENDING GUESTS
  // ===============================
  if (pendingGuests.length > 0) {
    csvSections.push(",=== PENDING INVITATIONS ===");
    csvSections.push(
      "S/No.,Guest Name,Email,Phone Number,RSVP Status,Date Sent"
    );

    pendingGuests.forEach((guest, index) => {
      csvSections.push(
        [
          `"${index + 1}"`,
          `"${guest.guestName || guest.name || ""}"`,
          `"${guest.email || ""}"`,
          `"${guest.phoneNumber || ""}"`,
          `"${
            guest.rsvpStatus
              ? guest.rsvpStatus.charAt(0).toUpperCase() +
                guest.rsvpStatus.slice(1)
              : "Pending"
          }"`,
          `"${formatDate(guest.dateSent) || ""}"`,
        ].join(",")
      );
    });

    csvSections.push("");
  }

  // ===============================
  // REFUNDS
  // ===============================
  if (refunds?.length > 0) {
    csvSections.push(",=== ATTIRE REFUND REQUESTS ===");
    csvSections.push(
      "S/No.,Request By,Email,Phone Number,Request Type,Refund Item,Amount,Request Date,Response Date,Status,"
    );

    refunds.forEach((refund, index) => {
      const guest = refund?.guestDetails || {};

      const fullName = [guest.firstName, guest.lastName]
        .filter(Boolean)
        .join(" ");

      

          const amountToRefund =
          refund.refundDetails.eventAttire?.reduce(
            (sum, item) => sum + (item.amountToRefund || 0),
            0
          ) || 0;

        const status = refund?.refundDetails?.refundStatus || "pending";

        const requestDate =refund.refundDetails.requestDate;
        const responseDate =refund.refundDetails.responseDate;

      csvSections.push(
        [
          `"${index + 1}"`,
          `"${fullName || ""}"`,
          `"${guest.email || ""}"`,
          `"${guest.phoneNumber || ""}"`,
          `"Refund"`,
          `"Event Attire"`,
          `"${formatCurrency(
            amountToRefund,
            refund.currency || event.preferredCurrency
          )}"`,
          `"${formatDate(requestDate) || ""}"`,
          `"${formatDate(responseDate) || ""}"`,

          `" ${status.charAt(0).toUpperCase() + status.slice(1)}"`,
        ].join(",")
      );
    });

    csvSections.push("");
  }

  // ===============================
  // FINANCIAL BREAKDOWN
  // ===============================
  csvSections.push(",=== FINANCIAL BREAKDOWN ===");
  csvSections.push(",Category,Amount");
  csvSections.push(`,Total Cash Gifts,${totalCashGifts}`);
  csvSections.push(`,Total Event Attire Sales,${totalEventAttire}`);
  csvSections.push(`,Total Ticket Sales,${totalTickets}`);
  csvSections.push(`,Total Revenue,${totalRevenue}`);
  csvSections.push(
    `,Pending Refund Amount,${refunds
      .filter((r) => r.status === "Pending")
      .reduce((sum, refund) => sum + (refund.amount || 0), 0)}`
  );
  csvSections.push(
    `,Approved Refund Amount,${refunds
      .filter((r) => r.status === "Approved" || r.status === "Paid")
      .reduce((sum, refund) => sum + (refund.amount || 0), 0)}`
  );
  csvSections.push(
    `,Net Revenue,${
      totalRevenue -
      refunds
        .filter((r) => r.status === "Approved" || r.status === "Paid")
        .reduce((sum, refund) => sum + (refund.amount || 0), 0)
    }`
  );

  // ===============================
  // GUEST BREAKDOWN
  // ===============================
  csvSections.push("");
  csvSections.push(",=== GUEST BREAKDOWN ===");
  csvSections.push(",Category,Count,Percentage");

  const totalGuests =
    acceptedGuests.length + declinedGuests.length + pendingGuests.length;

  csvSections.push(
    `,Accepted,${acceptedGuests.length},${(
      (acceptedGuests.length / totalGuests) *
      100
    ).toFixed(2)}%`
  );
  csvSections.push(
    `,Declined,${declinedGuests.length},${(
      (declinedGuests.length / totalGuests) *
      100
    ).toFixed(2)}%`
  );
  csvSections.push(
    `,Pending,${pendingGuests.length},${(
      (pendingGuests.length / totalGuests) *
      100
    ).toFixed(2)}%`
  );

  // ===============================
  // DOWNLOAD CSV
  // ===============================
  const csvContent = csvSections.join("\n");
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.download = `${(event?.eventTitle || "event")
    .replace(/[^a-z0-9]/gi, "_")
    .toLowerCase()}-report-${new Date().toISOString().split("T")[0]}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
